"use client";
/**
 * MarkdownRenderer — renders markdown content with GFM + mermaid support.
 * Track C2.
 *
 * - During streaming: shows a 3-dot placeholder for mermaid blocks.
 * - After streaming:  delegates mermaid blocks to <MermaidRenderer>.
 * - Intercepts #suggested-prompt links → fires selectSuggestedPrompt CustomEvent.
 */
import { useState, useEffect, useMemo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { MermaidRenderer } from "./MermaidRenderer";

interface MarkdownRendererProps {
  content:     string;
  isStreaming?: boolean;
  /** Render mermaid diagrams with the light theme regardless of app theme
      (used by the print document so diagrams stay readable on paper). */
  forceLightTheme?: boolean;
}

function useThemeColors() {
  const [isLight, setIsLight] = useState(false);
  useEffect(() => {
    const check = () => setIsLight(document.documentElement.classList.contains("theme-light"));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return isLight;
}

export function MarkdownRenderer({ content, isStreaming = false, forceLightTheme = false }: MarkdownRendererProps) {
  const [error, setError] = useState<Error | null>(null);
  const isLight = useThemeColors();

  // Reset error when content changes
  useEffect(() => { setError(null); }, [content]);

  // Theme-aware colors
  const c = useMemo(() => isLight ? {
    codeBg:     "rgba(0,0,0,0.04)",
    codeBorder: "rgba(0,0,0,0.08)",
    codeText:   "rgba(0,0,0,0.88)",
    inlineBg:   "rgba(91,108,255,0.08)",
    inlineBorder: "rgba(91,108,255,0.15)",
    inlineText: "rgba(91,108,255,1)",
    tableBorder:"rgba(0,0,0,0.08)",
    thBg:       "rgba(91,108,255,0.08)",
    thText:     "rgba(0,0,0,0.88)",
    thBorder:   "rgba(91,108,255,0.2)",
    tdBorder:   "rgba(0,0,0,0.06)",
    tdText:     "rgba(0,0,0,0.75)",
    linkColor:  "rgba(91,108,255,1)",
    linkBg:     "rgba(91,108,255,0.06)",
    linkBorder: "rgba(91,108,255,0.15)",
  } : {
    codeBg:     "rgba(255,255,255,0.05)",
    codeBorder: "rgba(255,255,255,0.08)",
    codeText:   "rgba(255,255,255,0.85)",
    inlineBg:   "rgba(91,108,255,0.12)",
    inlineBorder: "rgba(91,108,255,0.2)",
    inlineText: "rgba(255,255,255,0.9)",
    tableBorder:"rgba(255,255,255,0.08)",
    thBg:       "rgba(91,108,255,0.18)",
    thText:     "rgba(255,255,255,0.92)",
    thBorder:   "rgba(91,108,255,0.3)",
    tdBorder:   "rgba(255,255,255,0.06)",
    tdText:     "rgba(255,255,255,0.75)",
    linkColor:  "rgba(255,255,255,0.9)",
    linkBg:     "linear-gradient(90deg, rgba(91,108,255,0.15), rgba(236,72,153,0.12))",
    linkBorder: "rgba(91,108,255,0.25)",
  }, [isLight]);

  // Memoize components — only recreate when streaming state or theme flips
  const components: Components = useMemo(
    () => ({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      code({ className, children, ...props }: any) {
        const match    = /language-(\w+)/.exec(className ?? "");
        const language = match?.[1] ?? "";
        const inline   = !match;
        const code     = String(children).replace(/\n$/, "");

        // Mermaid — streaming placeholder
        if (language === "mermaid" && !inline && isStreaming) {
          return (
            <div className="my-4 p-6 bg-muted/30 border border-border/50 rounded-lg flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                  <span className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: "0.2s" }} />
                  <span className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: "0.4s" }} />
                </div>
                <span className="text-sm text-muted-foreground">Generating diagram...</span>
              </div>
            </div>
          );
        }

        // Mermaid — render after stream complete
        if (language === "mermaid" && !inline && !isStreaming) {
          return <MermaidRenderer chart={code} forceLightTheme={forceLightTheme} />;
        }

        // Block code
        if (!inline) {
          return (
            <pre className="rounded-lg p-4 overflow-x-auto my-2 text-sm" style={{ background: c.codeBg, border: `1px solid ${c.codeBorder}` }}>
              <code className={className} style={{ color: c.codeText }} {...props}>{children}</code>
            </pre>
          );
        }

        // Inline code
        return (
          <code className="px-1.5 py-0.5 rounded text-sm" style={{ background: c.inlineBg, border: `1px solid ${c.inlineBorder}`, color: c.inlineText }} {...props}>
            {children}
          </code>
        );
      },

      table({ children }) {
        return (
          <div className="overflow-x-auto my-4 w-full">
            <table className="w-full border-collapse" style={{ borderRadius: '8px', overflow: 'hidden', border: `1px solid ${c.tableBorder}` }}>
              {children}
            </table>
          </div>
        );
      },

      th({ children }) {
        return (
          <th
            className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
            style={{
              background: c.thBg,
              color: c.thText,
              borderBottom: `1px solid ${c.thBorder}`,
            }}
          >
            {children}
          </th>
        );
      },

      td({ children }) {
        return (
          <td
            className="px-4 py-2.5 text-sm align-top"
            style={{
              borderBottom: `1px solid ${c.tdBorder}`,
              color: c.tdText,
            }}
          >
            {children}
          </td>
        );
      },

      ul({ children }) {
        return <ul className="my-2 space-y-1 list-disc ml-6">{children}</ul>;
      },

      ol({ children }) {
        return <ol className="my-2 space-y-1 list-decimal ml-6">{children}</ol>;
      },

      li({ children }) {
        return <li className="ml-0">{children}</li>;
      },

      a({ href, children }) {
        if (href === "#suggested-prompt") {
          return (
            <a
              href={href}
              className="suggested-prompt-link"
              onClick={(e) => {
                e.preventDefault();
                const text =
                  typeof children === "string"
                    ? children
                    : (e.currentTarget as HTMLElement).textContent ?? "";
                window.dispatchEvent(
                  new CustomEvent("selectSuggestedPrompt", { detail: { prompt: text, text } })
                );
              }}
            >
              {children}
            </a>
          );
        }

        return (
          <a
            href={href}
            className="themessage-link"
            style={{
              color: c.linkColor,
              textDecoration: 'none',
              background: c.linkBg,
              border: `1px solid ${c.linkBorder}`,
              borderRadius: '4px',
              padding: '3px 6px',
              fontSize: '0.92em',
              fontWeight: 500,
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      },

      mark({ children }) {
        return (
          <mark
            style={{
              background: "rgba(59,130,246,0.25)",
              color: "inherit",
              borderRadius: "2px",
              padding: "0 2px",
            }}
          >
            {children}
          </mark>
        );
      },
    }),
    [isStreaming, c, forceLightTheme]
  );

  if (error) {
    return (
      <div className="text-red-500 p-4 bg-red-50 dark:bg-red-900/20 rounded">
        <p className="font-semibold">Error rendering content</p>
        <p className="text-sm mt-2">{error.message}</p>
      </div>
    );
  }

  try {
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={components}>
        {content}
      </ReactMarkdown>
    );
  } catch (err) {
    setError(err as Error);
    return null;
  }
}
